import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, imageUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { MessageCircle, ChevronLeft } from "lucide-react";
import { Toaster } from "sonner";
import ShareButtons from "@/components/ShareButtons";

const Spec = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-6 py-4 border-b border-[var(--aq-border)]">
      <span className="overline">{label}</span>
      <span className="text-sm text-[var(--aq-ink)] text-right">{value}</span>
    </div>
  ) : null;

const ProductPage = () => {
  const { id } = useParams();
  const { settings } = useSettings();
  const [watch, setWatch] = useState(null);
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });

  useEffect(() => {
    http.get(`/watches/${id}`).then((r) => {
      setWatch(r.data);
      setActive(0);
    });
  }, [id]);

  if (!watch) {
    return <div className="max-w-7xl mx-auto px-6 py-32 text-[var(--aq-muted)]">Cargando…</div>;
  }

  const images = watch.images?.length ? watch.images : [];
  const cover = images[active];

  const handleZoomMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };

  const waNumber = (settings?.whatsapp_number || "").replace(/[^\d]/g, "");
  const waMessage = encodeURIComponent(
    `¡Hola AQ Watches! Me gustaría solicitar este reloj personalizado: ${watch.name} (${watch.collection}).`
  );
  const waLink = `https://wa.me/${waNumber}?text=${waMessage}`;

  return (
    <div data-testid="product-page" className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-10 lg:pt-16 pb-24">
      <Toaster richColors position="top-center" />
      <Link to="/catalog" data-testid="back-to-catalog" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[var(--aq-muted)] hover:text-black mb-10">
        <ChevronLeft size={14} /> Volver al Catálogo
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div className="lg:sticky lg:top-28 self-start space-y-6">
          <div
            className="relative aspect-square bg-[var(--aq-surface)] overflow-hidden cursor-zoom-in"
            onMouseEnter={() => setZoom(true)}
            onMouseLeave={() => setZoom(false)}
            onMouseMove={handleZoomMove}
            data-testid="product-image-zoom"
          >
            {cover ? (
              <img
                src={imageUrl(cover)}
                alt={watch.name}
                className="w-full h-full object-cover transition-transform duration-300"
                style={
                  zoom
                    ? { transform: `scale(1.8)`, transformOrigin: `${zoomPos.x}% ${zoomPos.y}%` }
                    : { transform: "scale(1)" }
                }
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[var(--aq-muted)]">Sin imagen</div>
            )}
            {watch.is_new_arrival && (
              <span className="absolute top-4 left-4 z-10 bg-white text-[10px] tracking-[0.28em] uppercase px-3 py-1 border border-black/10">
                Nuevo
              </span>
            )}
          </div>

          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`thumb-${i}`}
                  className={`aspect-square overflow-hidden border ${
                    i === active ? "border-black" : "border-transparent"
                  }`}
                >
                  <img src={imageUrl(img)} alt={`${watch.name} ${i}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-8">
          <div>
            <p className="overline mb-4">{watch.collection}</p>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] mb-5">{watch.name}</h1>
            {watch.pricing_mode === "quote" ? (
              <p className="text-xl text-[var(--aq-ink)]">Solicitar cotización</p>
            ) : watch.price ? (
              <p className="text-xl text-[var(--aq-ink)]">{watch.price}</p>
            ) : null}
          </div>

          {watch.description && (
            <p className="text-[var(--aq-muted)] leading-relaxed">{watch.description}</p>
          )}

          <div>
            <Spec label="Movimiento" value={watch.movement} />
            <Spec label="Cristal" value={watch.crystal} />
            <Spec label="Tamaño de Caja" value={watch.case_size} />
            <Spec label="Resistencia al Agua" value={watch.water_resistance} />
            <Spec label="Pulsera" value={watch.bracelet} />
          </div>

          <div className="pt-4 space-y-4">
            <a
              data-testid="request-whatsapp-cta"
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="btn-whatsapp w-full"
            >
              <MessageCircle size={18} /> Solicitar este Reloj
            </a>
            <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--aq-muted)] text-center">
              Abre WhatsApp · Consulta privada
            </p>
          </div>

          <ShareButtons watch={watch} />
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
